import React, { useEffect, useMemo, useState } from 'react';
import { Check, Clock, X } from 'lucide-react';
import { getToken } from '../utils/auth';
import {
  acceptAppointmentRequest,
  declineAppointmentRequest,
} from '../api/appointmentsApi';
import {
  fromDatetimeLocalValue,
  toDatetimeLocalValue,
  zoomJoinErrorMessage,
  computeEndFromStartLocal,
  defaultDurationMinutes,
  APPOINTMENT_NOTES_MAX_LENGTH,
} from '../utils/appointmentHelpers';
import Modal, { DialogHeader, DialogFooter, DialogBody } from './ui/Modal';

const PendingAppointmentRequests = ({
  requests,
  patients,
  loading,
  highlightId,
  onRefresh,
  onChanged,
  onNotify,
  apiBaseUrl,
}) => {
  const [acceptTarget, setAcceptTarget] = useState(null);
  const [declineTarget, setDeclineTarget] = useState(null);
  const [acceptForm, setAcceptForm] = useState({
    startTime: '',
    endTime: '',
    duration: 30,
    appointmentType: 'Online',
    confirmation_notes: '',
  });
  const [declineReason, setDeclineReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const patientNameByMrn = useMemo(() => {
    const map = {};
    (patients || []).forEach((p) => {
      const name = `${p.firstName || ''} ${p.lastName || ''}`.trim();
      map[p.MRN] = name || `PMRN ${p.MRN}`;
    });
    return map;
  }, [patients]);

  useEffect(() => {
    if (!highlightId || !requests?.length) return;
    const el = document.getElementById(`pending-request-${highlightId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightId, requests]);

  const openAccept = (req) => {
    const duration = defaultDurationMinutes(req);
    const start = req.startTime ? new Date(req.startTime) : new Date();
    const end = req.endTime
      ? new Date(req.endTime)
      : new Date(start.getTime() + duration * 60_000);
    setAcceptForm({
      startTime: toDatetimeLocalValue(start),
      endTime: toDatetimeLocalValue(end),
      duration,
      appointmentType: req.appointmentType || 'Online',
      confirmation_notes: '',
    });
    setAcceptTarget(req);
  };

  const handleAcceptFormChange = (e) => {
    const { name, value } = e.target;
    setAcceptForm((prev) => {
      const next = { ...prev };
      if (name === 'duration') {
        const mins = Math.max(5, parseInt(value, 10) || 30);
        next.duration = mins;
        if (next.startTime) {
          next.endTime = computeEndFromStartLocal(next.startTime, mins);
        }
      } else if (name === 'startTime') {
        next.startTime = value;
        next.endTime = computeEndFromStartLocal(value, Number(next.duration) || 30);
      } else if (name === 'endTime') {
        next.endTime = value;
        if (next.startTime && value) {
          const startMs = new Date(next.startTime).getTime();
          const endMs = new Date(value).getTime();
          if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
            next.duration = Math.max(5, Math.round((endMs - startMs) / 60000));
          }
        }
      } else if (name === 'confirmation_notes') {
        next.confirmation_notes = String(value).slice(0, APPOINTMENT_NOTES_MAX_LENGTH);
      } else {
        next[name] = value;
      }
      return next;
    });
  };

  const handleAccept = async () => {
    if (!acceptTarget) return;
    const startIso = fromDatetimeLocalValue(acceptForm.startTime);
    const endIso = fromDatetimeLocalValue(acceptForm.endTime);
    if (!startIso || !endIso) {
      onNotify?.('Please set start and end times', 'error');
      return;
    }
    if (new Date(endIso) <= new Date(startIso)) {
      onNotify?.('End time must be after start time', 'error');
      return;
    }
    try {
      setSubmitting(true);
      const token = getToken();
      await acceptAppointmentRequest(apiBaseUrl, token, acceptTarget.id, {
        startTime: startIso,
        endTime: endIso,
        appointmentType: acceptForm.appointmentType || undefined,
        confirmation_notes: acceptForm.confirmation_notes || undefined,
      });
      setAcceptTarget(null);
      onNotify?.('Appointment request accepted', 'success');
      onChanged?.();
      onRefresh?.();
    } catch (e) {
      const msg = zoomJoinErrorMessage(e.message) || e.message;
      onNotify?.(msg === 'scheduling_conflict' ? 'That time conflicts with another appointment' : msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!declineTarget) return;
    try {
      setSubmitting(true);
      const token = getToken();
      await declineAppointmentRequest(
        apiBaseUrl,
        token,
        declineTarget.id,
        declineReason.trim() || null
      );
      setDeclineTarget(null);
      setDeclineReason('');
      onNotify?.('Appointment request declined', 'success');
      onChanged?.();
      onRefresh?.();
    } catch (e) {
      onNotify?.(e.message || 'Failed to decline', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!loading && (!requests || requests.length === 0)) {
    return null;
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border mb-8 overflow-hidden">
        <div className="px-6 py-4 border-b bg-amber-50/60 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Clock className="w-5 h-5 text-amber-700 shrink-0" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Pending patient requests</h2>
              <p className="text-sm text-gray-600">
                Review preferred times, then accept with a final schedule or decline.
              </p>
            </div>
          </div>
          {requests?.length > 0 && (
            <span className="shrink-0 rounded-full bg-amber-600 text-white text-xs font-bold px-2.5 py-1">
              {requests.length}
            </span>
          )}
        </div>

        {loading ? (
          <div className="px-6 py-10 text-center text-sm text-gray-500">Loading requests…</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {requests.map((req) => {
              const name = patientNameByMrn[req.PMRN] || `PMRN ${req.PMRN}`;
              const highlighted = highlightId != null && Number(highlightId) === Number(req.id);
              return (
                <li
                  key={req.id}
                  id={`pending-request-${req.id}`}
                  className={`px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-4 ${
                    highlighted ? 'bg-amber-50 ring-1 ring-inset ring-amber-200' : ''
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{name}</p>
                    <p className="text-sm text-gray-700 mt-0.5">
                      {req.title || 'Appointment'} · {req.appointmentType || '—'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Preferred:{' '}
                      {req.startTime
                        ? new Date(req.startTime).toLocaleString()
                        : '—'}
                      {req.endTime
                        ? ` – ${new Date(req.endTime).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}`
                        : ''}
                    </p>
                    {req.confirmation_notes && (
                      <p className="text-xs text-gray-600 mt-1 italic">
                        “{req.confirmation_notes}”
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => openAccept(req)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-600 text-white text-sm hover:bg-teal-700"
                    >
                      <Check className="w-4 h-4" />
                      Accept
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDeclineReason('');
                        setDeclineTarget(req);
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
                    >
                      <X className="w-4 h-4" />
                      Decline
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <Modal
        open={!!acceptTarget}
        onClose={() => setAcceptTarget(null)}
      >
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 max-w-md w-full max-h-[min(90vh,720px)] overflow-hidden flex flex-col">
            <DialogHeader
              title="Accept request"
              subtitle="Set the final appointment time. The patient will be notified."
              onClose={() => setAcceptTarget(null)}
              tone="muted"
            />
            <DialogBody className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start</label>
                <input
                  type="datetime-local"
                  name="startTime"
                  value={acceptForm.startTime}
                  onChange={handleAcceptFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                <select
                  name="duration"
                  value={acceptForm.duration}
                  onChange={handleAcceptFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                >
                  {[15, 20, 30, 45, 60, 90, 120].map((m) => (
                    <option key={m} value={m}>{m} min</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End</label>
                <input
                  type="datetime-local"
                  name="endTime"
                  value={acceptForm.endTime}
                  onChange={handleAcceptFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  name="appointmentType"
                  value={acceptForm.appointmentType}
                  onChange={handleAcceptFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                >
                  <option value="Online">Online</option>
                  <option value="In-Person">In-Person</option>
                  <option value="Emergency">Emergency</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note to patient (optional)
                </label>
                <textarea
                  name="confirmation_notes"
                  rows={2}
                  value={acceptForm.confirmation_notes}
                  onChange={handleAcceptFormChange}
                  maxLength={APPOINTMENT_NOTES_MAX_LENGTH}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                />
                <p className="mt-1 text-xs text-gray-500 text-right">
                  {String(acceptForm.confirmation_notes || '').length}/{APPOINTMENT_NOTES_MAX_LENGTH}
                </p>
              </div>
            </DialogBody>
            <DialogFooter>
              <button
                type="button"
                onClick={() => setAcceptTarget(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAccept}
                disabled={submitting}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                {submitting ? 'Saving…' : 'Confirm accept'}
              </button>
            </DialogFooter>
          </div>
      </Modal>

      <Modal
        open={!!declineTarget}
        onClose={() => setDeclineTarget(null)}
      >
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full border border-gray-100 overflow-hidden flex flex-col max-h-[min(90vh,720px)]">
            <DialogHeader
              title="Decline request"
              subtitle="Optionally add a reason. The patient will be notified."
              onClose={() => setDeclineTarget(null)}
              tone="muted"
            />
            <DialogBody className="px-6 py-4 space-y-3">
              <textarea
                rows={3}
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="Reason (optional)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              />
            </DialogBody>
            <DialogFooter>
              <button
                type="button"
                onClick={() => setDeclineTarget(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDecline}
                disabled={submitting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {submitting ? 'Declining…' : 'Decline'}
              </button>
            </DialogFooter>
          </div>
      </Modal>
    </>
  );
};

export default PendingAppointmentRequests;
